import React from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import { canEditProfile, getTermsAccepted, getUserId, isAdmin } from "../utils/auth";
import { isAdminStaffProfileSetupComplete } from "../utils/adminProfileSetup";
import {
    clearCompanyLogoCache,
    fetchCompanyLogoBlobUrl,
    resolveCompanyLogoDisplayUrl,
    useMyAvatar,
} from "../utils/profilePictureUtils";
import ProfileAvatar from "./ProfileAvatar";
import { formatRoleLabel } from "../utils/roleLabels";
import s from "../styles/ProfileHeader.module.css";

type ProfileData = {
    firstName?: string;
    lastName?: string;
    email?: string;
    telephone?: string;
    title?: string;
    company?: string;
    cleverSchoolName?: string;
    isClever?: boolean;
    role?: string;
    displayName?: string;
    profileImageUrl?: string;
    companyLogoUrl?: string;
};

type Props = {
    loadUrl?: string;
    data?: Partial<ProfileData>;
    onEdit?: () => void;
    className?: string;
    mapResponse?: (json: any) => ProfileData;
    profileUserId?: string; // Add profileUserId prop to check permissions
};

const defaultMap = (j: any): ProfileData => {
    const isClever = j.authProvider === "clever";
    return {
        firstName: j.firstName,
        lastName: j.lastName,
        email: j.email,
        telephone: j.telephone,
        title: j.title,
        company: j.cleverSchoolName ?? j.companyName ?? j.company,
        cleverSchoolName: j.cleverSchoolName,
        isClever,
        role: j.role,
        displayName: j.displayName,
        profileImageUrl: j.profileImageUrl,
        companyLogoUrl: j.companyLogoUrl,
    };
};

const ProfileHeader = React.memo(function ProfileHeader({
    onEdit,
    className = "",
    mapResponse = defaultMap,
    loadUrl,
    profileUserId,
}: Props) {
    const navigate = useNavigate();

    const loggedInUserId = React.useMemo(() => getUserId(), []);

    // Don't construct API URL if userId is invalid
    const effectiveLoadUrl = React.useMemo(() => {
        if (loadUrl) return loadUrl;
        if (!loggedInUserId) return null;
        return buildApiUrl('api/admin-profile');
    }, [loadUrl, loggedInUserId]);

    const canEdit = React.useMemo(() => canEditProfile(profileUserId), [profileUserId]);

    const isOwnAdminProfile = !profileUserId;
    const { avatarUrl: myAvatarUrl } = useMyAvatar(
        isOwnAdminProfile && getTermsAccepted()
    );

    const [data, setData] = React.useState<ProfileData>();
    const [loading, setLoading] = React.useState<boolean>(!!effectiveLoadUrl);
    const [error, setError] = React.useState<Error | null>(null);
    const [companyLogoBlobUrl, setCompanyLogoBlobUrl] = React.useState<string | null>(null);
    const [companyLogoFailed, setCompanyLogoFailed] = React.useState(false);

    // Memoize mapResponse with empty deps to maintain stable reference across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableMapResponse = React.useCallback(mapResponse, []);

    const loadCompanyLogoBlob = React.useCallback(async (email?: string, bustCache = false) => {
        if (!email || !isAdmin()) {
            setCompanyLogoBlobUrl(null);
            return;
        }
        if (bustCache) {
            clearCompanyLogoCache(email);
        }
        try {
            const blob = await fetchCompanyLogoBlobUrl(email, bustCache);
            setCompanyLogoBlobUrl(blob);
        } catch {
            setCompanyLogoBlobUrl(null);
        }
    }, []);

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            // Check if terms have been accepted before making API calls
            const hasAcceptedTerms = getTermsAccepted();
            if (!hasAcceptedTerms) {
                if (!cancelled) {
                    setLoading(false);
                }
                return;
            }

            if (!loggedInUserId || loggedInUserId === "0") {
                if (!cancelled) {
                    setError(new Error('Invalid user ID. Please log in again.'));
                    setLoading(false);
                }
                return;
            }

            if (!effectiveLoadUrl) {
                if (!cancelled) {
                    setError(new Error('Unable to load profile'));
                    setLoading(false);
                }
                return;
            }

            try {
                if (!cancelled) {
                    setLoading(true);
                    setError(null);
                }

                const res = await fetch(effectiveLoadUrl, {
                    method: "GET",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    credentials: "include" // Use HTTP-only cookie for auth
                });

                if (res.status === 404) {
                    if (!cancelled) setData({});
                    return;
                }

                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                if (!cancelled) {
                    const isOwnAdminProfile =
                        !profileUserId &&
                        !!effectiveLoadUrl?.includes("api/admin-profile");
                    if (
                        isOwnAdminProfile &&
                        !isAdminStaffProfileSetupComplete({
                            title: json.title,
                            telephone: json.telephone,
                            companyName: json.companyName,
                            cleverSchoolName: json.cleverSchoolName,
                            email: json.email,
                        })
                    ) {
                        navigate("/add-admin-profile", {
                            replace: true,
                            state: { firstTimeSetup: true, createProfile: true },
                        });
                        return;
                    }
                    setData(stableMapResponse(json));
                }
            } catch (e: any) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [effectiveLoadUrl, stableMapResponse, loggedInUserId, profileUserId, navigate]);

    // Listen for termsAccepted event to load data after terms are accepted
    React.useEffect(() => {
        const handleTermsAccepted = () => {
            // Reload the profile data after terms are accepted
            (async () => {
                if (!effectiveLoadUrl) return;

                try {
                    setLoading(true);
                    setError(null);
                    const res = await fetch(effectiveLoadUrl, {
                        method: "GET",
                        headers: { "Content-Type": "application/json", Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    });

                    if (res.status === 404) {
                        setData({});
                        return;
                    }

                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const json = await res.json();
                    setData(stableMapResponse(json));
                } catch (e: any) {
                    setError(e);
                } finally {
                    setLoading(false);
                }
            })();
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => window.removeEventListener('termsAccepted', handleTermsAccepted);
    }, [effectiveLoadUrl, stableMapResponse]);

    // Listen for profilePictureUpdated event to refresh avatar after upload
    React.useEffect(() => {
        const handleProfilePictureUpdated = () => {
            // Reload the profile data after profile picture is updated
            (async () => {
                if (!effectiveLoadUrl) return;

                try {
                    const res = await fetch(effectiveLoadUrl, {
                        method: "GET",
                        headers: { "Content-Type": "application/json", Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    });

                    if (res.status === 404) {
                        setData({});
                        return;
                    }

                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const json = await res.json();
                    setData(stableMapResponse(json));
                    const email = json.email ?? json.username;
                    if (email) {
                        void loadCompanyLogoBlob(email, true);
                    }
                } catch (e: any) {
                    console.error('Failed to reload profile after picture update:', e);
                }
            })();
        };

        window.addEventListener('profilePictureUpdated', handleProfilePictureUpdated);
        return () => window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdated);
    }, [effectiveLoadUrl, stableMapResponse, loadCompanyLogoBlob]);

    React.useEffect(() => {
        setCompanyLogoFailed(false);
        if (!data?.companyLogoUrl) {
            setCompanyLogoBlobUrl(null);
        }
        if (data?.email) {
            void loadCompanyLogoBlob(data.email, true);
        }
    }, [data?.email, data?.companyLogoUrl, loadCompanyLogoBlob]);

    const handleCompanyLogoError = React.useCallback(() => {
        setCompanyLogoFailed(true);
    }, []);

    React.useEffect(() => {
        const handleProfileUpdated = async () => {
            if (!effectiveLoadUrl) return;
            try {
                const res = await fetch(effectiveLoadUrl, {
                    method: "GET",
                    headers: { "Content-Type": "application/json", Accept: "application/json" },
                    credentials: "include",
                });
                if (res.ok) {
                    const json = await res.json();
                    setData(stableMapResponse(json));
                    const email = json.email ?? json.username;
                    if (email) {
                        void loadCompanyLogoBlob(email, true);
                    }
                }
            } catch (e) {
                console.error("Failed to reload profile after update:", e);
            }
        };
        window.addEventListener("profileUpdated", handleProfileUpdated);
        return () => window.removeEventListener("profileUpdated", handleProfileUpdated);
    }, [effectiveLoadUrl, stableMapResponse, loadCompanyLogoBlob]);

    const handleEdit = React.useCallback(() => {
        if (onEdit) {
            onEdit();
        } else {
            localStorage.setItem("loggedInUserEdit", 'true');
            navigate("/add-admin-profile");
        }
    }, [onEdit, navigate]);

    if (loading) {
        return (
            <section className={`${s.wrap} staff-page-shell ${className}`}>
                <div className={s.inner}>
                    <div className={s.skel}>
                        <div className={s.skelAvatar} />
                        <div className={s.skelBlock}>
                            <div className={`${s.skelLine} ${s.w60}`} />
                            <div className={`${s.skelLine} ${s.w30}`} />
                            <div className={`${s.skelLine} ${s.w80}`} />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className={`${s.wrap} staff-page-shell ${className}`}>
                <div className={s.inner}>
                    <div className={s.error}>Error: {error.message}</div>
                </div>
            </section>
        );
    }

    if (!data) return null;

    const name =
        data.displayName ||
        [data.firstName, data.lastName].filter(Boolean).join(" ") ||
        "—";

    const roleLabel = formatRoleLabel(data.role);
    const roleClass = roleLabel
        ? s.badge
        : s.badgeNeutral;

    const companyLogoDisplayUrl = resolveCompanyLogoDisplayUrl(
        data.companyLogoUrl,
        companyLogoBlobUrl
    );

    return (
        <section className={`${s.wrap} staff-page-shell ${className}`} aria-labelledby="profile-name">
            <div className={s.inner}>
                {/* Conditionally show edit button based on permissions */}
                {canEdit && (
                    <button
                        type="button"
                        className={s.edit}
                        aria-label="Edit profile"
                        onClick={handleEdit}
                        title="Edit profile"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/>
                        </svg>
                    </button>
                )}

                <div className={s.headerRow}>
                    <div className={s.avatarCol}>
                        <ProfileAvatar
                            size="large"
                            className={s.avatar}
                            allowUpload={false}
                            alt="Profile picture"
                            displayName={name}
                            imageUrl={data.profileImageUrl}
                            previewUrl={isOwnAdminProfile ? (myAvatarUrl ?? undefined) : undefined}
                        />
                    </div>

                    <div className={s.mainCol}>
                        <div className={s.titleBlock}>
                            <h1 id="profile-name" className={s.name}>{name}</h1>
                        </div>

                        <div className={s.rule} />

                        <div className={s.grid}>
                            {/* Row 1 */}
                            <div className={s.field}>
                                <span className={s.label}>Email</span>
                                <span className={s.value}>{data.email ?? "—"}</span>
                            </div>
                            <div className={s.field}>
                                <span className={s.label}>Phone Number</span>
                                <span className={s.value}>{data.telephone ?? "—"}</span>
                            </div>
                            <div className={s.field}>
                                <span className={s.label}>Account Access</span>
                                <span className={roleClass}>{roleLabel}</span>
                            </div>

                            {/* Row 2 */}
                            <div className={s.field}>
                                <span className={s.label}>Title</span>
                                <span className={s.value}>{data.title ?? "—"}</span>
                            </div>
                            <div className={s.field}>
                                <span className={s.label}>{data.isClever ? "School Name" : "Company Name"}</span>
                                <span className={s.value}>{data.company ?? "—"}</span>
                            </div>
                            {isAdmin() && companyLogoDisplayUrl && !companyLogoFailed && (
                                <div className={s.field}>
                                    <span className={s.label}>Company Logo</span>
                                    <img
                                        src={companyLogoDisplayUrl}
                                        alt=""
                                        className={s.companyLogoImg}
                                        onError={handleCompanyLogoError}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});

export default ProfileHeader;
